import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH } from '@fortawesome/pro-regular-svg-icons';

import { getShopProductUrl } from '../../helpers/helpers';
import { formatDate, getAgentSummary, getLambdaSummary } from '../../helpers/url_data_agent_evaluations_helpers';
import {
  formatConfidence,
  formatPrice,
  getApplyDecisionSummary,
  getLiveMatchActions,
  getLiveMatchAutomationReason,
  getLiveMatchConfidence,
  getLiveMatchConfidenceBand,
  getLiveMatchHandoffReason,
  getLiveMatchProduct,
  getLiveMatchReason,
  getLiveMatchReviewReason,
  getLiveMatchTimestamp,
  getProductAdminUrl,
  getUrlDataAdminUrl,
  isCatalogBrandCreation,
  isLiveMatchBrandMismatch,
  isPreownedSource,
  isProductCreation,
  isProductCreationProcessingPending
} from '../../helpers/url_data_agent_live_matches_helpers';
import { getUrlDataLink } from '../../helpers/url_data_helpers';
import PreviewThumb from './PreviewThumb';

import './LiveMatchRow.scss';

const LiveMatchRow = ({ row, applyControl, focused, isBusy, selectable, selected, onToggleSelect, onApply, onCorrect, onSendToHuman, onToggleDetail, onTagClick }) => {
  // Display data
  const source = getLambdaSummary(row);
  const agent = getAgentSummary(row);
  const isCreation = isProductCreation(row);
  const product = getLiveMatchProduct(row);
  const decision = getApplyDecisionSummary(row, applyControl);
  const handoffReason = getLiveMatchHandoffReason(row);
  const reviewReason = getLiveMatchReviewReason(row);
  const automationReason = getLiveMatchAutomationReason(row, applyControl);
  const confidence = getLiveMatchConfidence(row);
  const band = getLiveMatchConfidenceBand(confidence);
  const brandMismatch = isLiveMatchBrandMismatch(row);
  const sourceUrl = getUrlDataLink(row);
  const urlDataUrl = getUrlDataAdminUrl(row);
  const sourceTitle = source.productName || row.urlStem || 'Unknown page';
  const sourceBrand = source.brandName || null;
  const sourcePrice = formatPrice(row.productPrice, row.productCurrency);
  const productTitle = product?.title || (row.humanAction ? 'No product preview available' : handoffReason ? 'Awaiting catalog review' : agent.productName || (reviewReason ? 'Choose a catalog product' : '—'));
  const productBrand = product?.brand || (!row.humanAction && (row.currentApplyPolicy?.productToCreateBrand || agent.brandName)) || null;
  const productPrice = product ? formatPrice(product.fallbackPrice, product.fallbackPriceCurrency) : null;
  const reviewerName = row.latestReview?.reviewer?.name;
  const stateTooltip = [reviewerName, formatDate(getLiveMatchTimestamp(row))].filter(Boolean).join(' · ');

  const flags = [
    { label: row.createdAllBrandId ? 'Catalog brand created' : 'New catalog brand', on: isCatalogBrandCreation(row) },
    { label: 'Pre-owned', on: isPreownedSource(row) },
    { label: 'Used', on: product?.isUsed },
    { label: 'Hidden', on: product?.isHidden },
    { label: 'Product processing pending', on: isProductCreationProcessingPending(row) }
  ].filter(flag => flag.on);

  // Actions
  const handlers = { approve: onApply, correct: onCorrect, send_back: onSendToHuman, undo: onSendToHuman };
  const actions = getLiveMatchActions(row);

  return (
    <div className={cn('live-match-row', { focused, selected })}>
      {selectable && (
        <input
          type='checkbox'
          className='lm-select'
          checked={Boolean(selected)}
          disabled={isBusy || !actions.length}
          aria-label={`Select ${sourceTitle} for a bulk action`}
          onChange={onToggleSelect}
        />
      )}
      <PreviewThumb source={source.primaryImageUrl} title={sourceTitle} subtitle={sourceBrand} />
      <div className='lm-text'>
        <div className='lm-title' title={sourceTitle}>
          {sourceUrl ? (
            <a href={sourceUrl} target='_blank' rel='noopener noreferrer'>
              {sourceTitle}
            </a>
          ) : (
            sourceTitle
          )}
        </div>
        <div className='lm-meta'>
          <span>{row.domain || 'page'}</span>
          <span className={cn({ 'lm-brand-mismatch': brandMismatch })}>{sourceBrand || '—'}</span>
          {sourcePrice && <span>{sourcePrice}</span>}
          {urlDataUrl && (
            <a href={urlDataUrl} target='_blank' rel='noopener noreferrer'>
              ↗ URL data
            </a>
          )}
        </div>
        {(automationReason || handoffReason) && <div className='lm-reason' title={automationReason || handoffReason}>{automationReason || handoffReason}</div>}
      </div>
      <span className={cn('lm-glyph', { mismatch: brandMismatch })} aria-hidden='true'>
        {brandMismatch ? '≠' : '⇄'}
      </span>
      <PreviewThumb source={product?.image || (row.humanAction ? null : agent.primaryImageUrl)} title={productTitle} subtitle={productBrand} />
      <div className='lm-text'>
        <div className='lm-title' title={productTitle}>
          {product ? (
            <a href={getShopProductUrl(product.id)} target='_blank' rel='noopener noreferrer'>
              {productTitle}
            </a>
          ) : (
            productTitle
          )}
        </div>
        <div className='lm-meta'>
          <span className={cn({ 'lm-brand-mismatch': brandMismatch })}>{productBrand || '—'}</span>
          {(product?.category?.name || (isCreation && agent.categoryName)) && <span>{product?.category?.name || agent.categoryName}</span>}
          {productPrice && <span>{productPrice}</span>}
          {product ? (
            <a className='mono' href={getProductAdminUrl(product.id)} target='_blank' rel='noopener noreferrer'>
              #{product.id}
            </a>
          ) : (
            <span>{isCreation ? 'New product preview' : row.humanMatched_Product_id ? `Product #${row.humanMatched_Product_id}` : row.humanAction === 'dismissed' ? 'Link dismissed' : 'No catalog product linked'}</span>
          )}
        </div>
        {handoffReason && row.humanAction && (
          <div className='lm-reason'>
            {row.humanAction === 'new_product' ? 'Catalog team created this product' : row.humanAction === 'matched_existing' ? 'Catalog team matched an existing product' : 'Catalog team dismissed this link'}
          </div>
        )}
      </div>
      <div className='lm-flags'>
        {row.tag && (
          <button type='button' className='lm-pill' aria-label={`Search for ${row.tag}`} title='Search for this batch tag' onClick={() => onTagClick(row.tag)}>
            {row.tag}
          </button>
        )}
        {flags.map(flag => (
          <span key={flag.label} className='lm-pill'>
            {flag.label}
          </span>
        ))}
      </div>
      <span className={cn('lm-conf', band && `band-${band}`)} title={getLiveMatchReason(row) || undefined}>
        {formatConfidence(confidence)}
      </span>
      <span className='lm-state' title={stateTooltip}>
        {decision.label}
      </span>
      <div className='lm-actions'>
        {actions.map(action => (
          <button key={action.key} type='button' className={cn('se-btn', { primary: action.primary })} disabled={isBusy} onClick={handlers[action.key]}>
            {action.display}
          </button>
        ))}
        <button type='button' className='se-btn icon' aria-label='Details' title='Details' onClick={onToggleDetail}>
          <FontAwesomeIcon icon={faEllipsisH} />
        </button>
      </div>
    </div>
  );
};

LiveMatchRow.propTypes = {
  row: PropTypes.object.isRequired,
  applyControl: PropTypes.object,
  focused: PropTypes.bool,
  isBusy: PropTypes.bool,
  selectable: PropTypes.bool,
  selected: PropTypes.bool,
  onToggleSelect: PropTypes.func,
  onApply: PropTypes.func.isRequired,
  onCorrect: PropTypes.func.isRequired,
  onSendToHuman: PropTypes.func.isRequired,
  onToggleDetail: PropTypes.func.isRequired,
  onTagClick: PropTypes.func.isRequired
};

export default LiveMatchRow;
